import { Component, computed, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getAccountSubscription } from '@reducers/account/account-service.selectors';
import { AppState } from '@reducers/interfaces';
import { CustomFieldsAppliesTo, CustomFieldsModel } from '@reducers/orm/custom-fields/custom-fields.model';
import { getCustomFieldsByAppliesTo } from '@reducers/orm/custom-fields/custom-fields.selectors';
import { anyDepartmentHasPaidBreaks } from '@reducers/orm/department/department.service';
import {
  TimesheetColumns,
  TimesheetCustomFieldsColumns,
  TimesheetShowFilters,
  TimesheetStatusFilters,
  TimesheetTypeFilters,
} from '@reducers/page-filters/page-filters.model';
import { filterPersistenceDebounceTime, PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';
import { Subscription } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';

import { SidebarComponent } from '../../../../shared/sidebar/sidebar.component';

interface ColumnOption {
  field: string;
  label: string;
  showOnPeriod?: boolean;
}

@Component({
  standalone: false,
  selector: 'timesheet-sidebar',
  templateUrl: './timesheet-sidebar.component.html',
})
export class TimesheetSidebarComponent implements OnInit, OnDestroy, OnChanges {
  private sidebar = inject(SidebarComponent);
  private filterService = inject(PageFiltersService);
  private translate = inject(TranslateService);
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);

  // The categorized priority view (/timesheet/priority) only honours column toggles —
  // the show/status/type/conflict/custom-field filters don't apply there. Derived
  // from navigation as a signal so OnPush updates without a manual markForCheck.
  isListView = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.includes('/timesheet/priority')),
    ),
    { initialValue: this.router.url.includes('/timesheet/priority') },
  );

  @Input()
  period: string;
  @Input()
  columns: TimesheetColumns;
  @Input()
  show: TimesheetShowFilters;
  @Input()
  status: TimesheetStatusFilters;
  @Input()
  type: TimesheetTypeFilters;
  @Input()
  customFieldsColumns: TimesheetCustomFieldsColumns;

  @Input()
  periodPage = false;

  form = new UntypedFormGroup({
    show: new UntypedFormGroup({
      absence: new UntypedFormControl(false),
      schedule: new UntypedFormControl(false),
      employee: new UntypedFormControl(false),
    }),
    status: new UntypedFormGroup({
      approved: new UntypedFormControl(false),
      pending: new UntypedFormControl(false),
      declined: new UntypedFormControl(false),
    }),
    type: new UntypedFormGroup({
      activeClock: new UntypedFormControl(false),
      clocked: new UntypedFormControl(false),
      manual: new UntypedFormControl(false),
      onlyConflict: new UntypedFormControl(false),
    }),
    columns: new UntypedFormGroup({
      break: new UntypedFormControl(false),
      paidBreak: new UntypedFormControl(false),
      location: new UntypedFormControl(false),
      department: new UntypedFormControl(false),
      team: new UntypedFormControl(false),
      shift: new UntypedFormControl(false),
      note: new UntypedFormControl(false),
      km: new UntypedFormControl(false),
      meals: new UntypedFormControl(false),
      surcharge: new UntypedFormControl(false),
    }),
  });

  showOptions: ColumnOption[] = [
    { field: 'schedule', label: this.translate.instant('Show schedules'), showOnPeriod: false },
    { field: 'absence', label: this.translate.instant('Show absence'), showOnPeriod: true },
    { field: 'employee', label: this.translate.instant('Show all employees'), showOnPeriod: true },
  ];

  statusOptions: ColumnOption[] = [
    { field: 'approved', label: this.translate.instant('Approved') },
    { field: 'pending', label: this.translate.instant('Pending') },
    { field: 'declined', label: this.translate.instant('Declined') },
  ];

  typeOptions: ColumnOption[] = [{ field: 'manual', label: this.translate.instant('Other') }];

  conflictOptions: ColumnOption[] = [{ field: 'onlyConflict', label: this.translate.instant('Only show conflicts') }];

  isPaidBreaksEnabled = this.store.selectSignal(anyDepartmentHasPaidBreaks);

  #accountSubscription = this.store.selectSignal(getAccountSubscription);

  columnOptions = computed<ColumnOption[]>(() => {
    const paidBreaksEnabled = this.isPaidBreaksEnabled();
    const accountSubscription = this.#accountSubscription();
    const breakLabel = paidBreaksEnabled ? this.translate.instant('Unpaid break') : this.translate.instant('Break');

    const options: ColumnOption[] = [{ field: 'break', label: breakLabel }];

    if (paidBreaksEnabled) {
      options.push({ field: 'paidBreak', label: this.translate.instant('Paid break') });
    }

    options.push(
      { field: 'location', label: this.translate.instant('Location') },
      { field: 'department', label: this.translate.instant('Department') },
      { field: 'team', label: this.translate.instant('Team') },
      { field: 'shift', label: this.translate.instant('Shift') },
      { field: 'note', label: this.translate.instant('Note') },
    );

    if (accountSubscription && hasAtLeastSubscriptionPlan(PlanType.BASIC, accountSubscription)) {
      options.push({ field: 'surcharge', label: this.translate.instant('Rate card') });
    }

    if (accountSubscription && hasAtLeastSubscriptionPlan(PlanType.PREMIUM, accountSubscription)) {
      options.push({ field: 'km', label: this.translate.instant('Km') });
      options.push({ field: 'meals', label: this.translate.instant('Meals') });
    }

    return options;
  });

  customFieldOptions: ColumnOption[] = [];

  private subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      this.form.valueChanges.pipe(debounceTime(filterPersistenceDebounceTime)).subscribe((formValue) => {
        this.filterService.setFilters('timesheet', formValue);
      }),
    );

    this.subscription.add(
      this.store
        .pipe(
          select(getAccountSubscription),
          filter((subscription) => !!subscription),
          take(1),
        )
        .subscribe((accountSubscription) => {
          if (hasAtLeastSubscriptionPlan(PlanType.BASIC, accountSubscription)) {
            this.typeOptions.push({ field: 'activeClock', label: this.translate.instant('Currently clocked in') });
            this.typeOptions.push({ field: 'clocked', label: this.translate.instant('Clocked hours') });
          }
        }),
    );

    this.subscription.add(
      this.store
        .select(getCustomFieldsByAppliesTo(CustomFieldsAppliesTo.TIMESHEET))
        .subscribe((customFields: CustomFieldsModel[]) => {
          this.setupCustomFieldOptions(customFields);
        }),
    );
  }

  private setupCustomFieldOptions(customFields: CustomFieldsModel[]) {
    const customFieldOptions: ColumnOption[] = [];
    const formGroup = new UntypedFormGroup({});
    customFields.forEach((customField) => {
      customFieldOptions.push({ field: customField.id.toString(), label: customField.name });
      formGroup.addControl(customField.id.toString(), new UntypedFormControl(false));
    });

    if (this.form.contains('customFields')) {
      this.form.removeControl('customFields');
    }

    if (this.customFieldsColumns) {
      formGroup.patchValue(this.customFieldsColumns, { emitEvent: false });
    }
    this.form.addControl('customFields', formGroup);
    this.customFieldOptions = customFieldOptions;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']) {
      this.form.controls['columns'].patchValue(changes['columns'].currentValue, { emitEvent: false });
    }

    if (changes['show']) {
      this.form.controls['show'].patchValue(changes['show'].currentValue, { emitEvent: false });
    }

    if (changes['type']) {
      this.form.controls['type'].patchValue(changes['type'].currentValue, { emitEvent: false });
    }

    if (changes['status']) {
      this.form.controls['status'].patchValue(changes['status'].currentValue, { emitEvent: false });
    }

    if (changes['customFieldsColumns'] && this.form.contains('customFields')) {
      this.form.controls['customFields'].patchValue(changes['customFieldsColumns'].currentValue);
    }
  }

  close() {
    this.sidebar.close();
  }
}
